import { useState } from 'react';
import type { Conversation } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { messageStore } from '@/lib/messageStore';
import { DialogFooter, ErrorBox, Field, Input, Modal, Toggle, ask } from '@/components/ui';

const refresh = (id: string, ws: string) => {
  void queryClient.invalidateQueries({ queryKey: qk.conversation(id) });
  void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
  void queryClient.invalidateQueries({ queryKey: ['closed-rooms'] });
  void messageStore.catchUp(id);
};

/** Close: confirm, post "X closed the room", end any call; the room moves to Closed rooms. */
export async function closeRoom(conv: Conversation, ws: string) {
  const ok = await ask({
    title: `Close “${conv.title ?? 'this ad-hoc meeting room'}”?`,
    body: <div className="space-y-2" data-testid="close-room-confirm">
      <p>Everyone in the ad-hoc meeting room sees “closed the ad-hoc meeting room”, any call running in it ends, and it leaves the chat list.</p>
      <p>The messages are kept: find it under <strong>Closed ad-hoc meeting rooms</strong> to read it or reopen it.</p>
    </div>,
    confirmLabel: 'Close ad-hoc meeting room',
  });
  if (!ok) return false;
  try {
    await api.closeRoom(conv.id);
    refresh(conv.id, ws);
    useUi.getState().toast('Ad-hoc meeting room closed', 'success');
    return true;
  } catch (e) { useUi.getState().toast(errorMessage(e), 'error'); return false; }
}

export async function reopenRoom(conv: Conversation, ws: string) {
  try {
    await api.reopenRoom(conv.id);
    refresh(conv.id, ws);
    useUi.getState().toast('Ad-hoc meeting room reopened', 'success');
  } catch (e) { useUi.getState().toast(errorMessage(e), 'error'); }
}

/** Creator (or "Close any room"): rename, and whether members may add people. */
export function RoomSettingsModal({ conv, onClose }: { conv: Conversation; onClose: () => void }) {
  const [title, setTitle] = useState(conv.title ?? '');
  const [canAdd, setCanAdd] = useState(conv.members_can_add ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const save = async () => {
    setBusy(true);
    try {
      await api.updateConversation(conv.id, { title: title.trim(), members_can_add: canAdd });
      refresh(conv.id, conv.workspace_id);
      onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Ad-hoc meeting room settings"
      footer={<DialogFooter onCancel={onClose} onSave={() => void save()} saving={busy} disabled={!title.trim()} />}>
      <div className="space-y-4">
        <Field label="Ad-hoc meeting room name"><Input value={title} maxLength={80} onChange={e => setTitle(e.target.value)} /></Field>
        <Toggle checked={canAdd} onChange={setCanAdd} label="Members can add people" />
        <p className="text-xs text-slate-500">When this is off, only you (and admins) can add people.</p>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}
